// The structured discharge summary (Part F), including the death-summary
// variant. Phase 5 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.9/§6.11).
//
// Same medico-legal shape every other signed document in this workspace
// uses: a draft is freely editable in place by its author, signing freezes
// it, and a correction afterward is a new row (amend) — never an in-place
// edit of signed text. Discharge medications are prefilled from active
// orders for the doctor to confirm, never auto-signed (design doc §6.11's
// own words) — there is no code path that writes this table from the AI
// draft or from the medication list without a save click in between.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { ApiError } from '@/lib/api/client';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { appendText, mergeTextFields, selectField, textField, vBool, vOneOf, vText, yesNoField, type Extracted } from '@/lib/voiceFill';
import { getAdmission } from '@/api';
import DischargeSummaryChat, { type Admission as ChatAdmission } from '@/components/DischargeSummaryChat';
import DocumentPreviewButtons from '@/components/DocumentPreviewButtons';
import {
  useDischargeSummary, usePutDischargeSummary, useSignDischargeSummary, useAmendDischargeSummary,
  useDiagnoses, useMedicationOrders,
} from '../hooks';
import type {
  DischargeType, DischargeMedicationRow, FollowUpRow, PendingResultRow, DischargeSummaryInput,
  DischargeSummary, AdmissionDiagnosis,
} from '../types';
import { Section, Empty, fmtDateTime, useCan } from '../ui';

// The full text last approved in the AI chat — see ai_generated_text's own
// comment in types.ts. A plain, collapsed-by-default reference, never an
// editable field: the structured fields above/below it remain the one
// place a doctor actually edits this record.
function GeneratedTextDetails({ text, hint }: { text: string; hint?: string }) {
  return (
    <details className="rounded-lg border border-border bg-muted/30 p-2.5">
      <summary className="cursor-pointer text-[12px] font-semibold text-ink-muted">
        📄 Full text generated by the AI assistant
      </summary>
      {hint && <div className="mb-2 mt-1.5 text-[11px] text-ink-muted">{hint}</div>}
      <div className="mt-1.5 max-h-80 overflow-y-auto whitespace-pre-wrap rounded border border-border bg-card p-2.5 text-[12px]">
        {text}
      </div>
    </details>
  );
}

// Renders `**bold**`/`*bold*` spans inline — the same two shapes the rest
// of this app's markdown-lite documents use (docxGenerator.js,
// DocumentEditor's documentModel.ts), never a general markdown parser.
function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part)) return <strong key={i}>{part.slice(1, -1)}</strong>;
    return <span key={i}>{part}</span>;
  });
}

type ReadableBlock = { type: 'blank' } | { type: 'heading' | 'line'; text: string };

// Same bare-label heading detection as utils/dischargeSummaryDraft.js's
// splitSections() (and, before that, utils/aiEditObservation.js's
// extractSections()) — mirrored here rather than shared across a network
// call, same "mirror per consumer" precedent as documentSections.ts.
// One source line stays one rendered line (DocumentEditor's own contract:
// "one markdown line = one paragraph, always") — this never reflows text
// into justified paragraphs, which would silently change what the doctor
// approved looks like.
function parseReadableBlocks(text: string): ReadableBlock[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks: ReadableBlock[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      const last = blocks[blocks.length - 1];
      if (last && last.type !== 'blank') blocks.push({ type: 'blank' });
      continue;
    }
    const markdown = line.match(/^#{1,6}\s+(.+)$/);
    const wrapped = line.match(/^(\*{1,2}|__)(.+?)\1:?$/);
    const bareLabel = /^[A-Za-z][^:]{0,40}:$/.test(line);
    if (markdown && markdown[1]) blocks.push({ type: 'heading', text: markdown[1] });
    else if (wrapped && wrapped[2]) blocks.push({ type: 'heading', text: wrapped[2] });
    else if (bareLabel) blocks.push({ type: 'heading', text: line.slice(0, -1) });
    else blocks.push({ type: 'line', text: line });
  }
  while (blocks.length && blocks[0]?.type === 'blank') blocks.shift();
  while (blocks.length && blocks[blocks.length - 1]?.type === 'blank') blocks.pop();
  return blocks;
}

/** The prominent, readable rendering of the approved document — see the
 *  module header comment for why this replaced always-visible structured
 *  fields as the default view. */
function ReadableDocument({ text }: { text: string }) {
  const blocks = useMemo(() => parseReadableBlocks(text), [text]);
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      {blocks.map((b, i) => {
        if (b.type === 'blank') return <div key={i} className="h-2" />;
        if (b.type === 'heading') {
          return (
            <div key={i} className="mb-1 mt-3 text-[11px] font-bold uppercase tracking-wide text-ink-muted first:mt-0">
              {b.text}
            </div>
          );
        }
        return <div key={i} className="text-[13px] leading-relaxed">{renderInline(b.text)}</div>;
      })}
    </div>
  );
}

const MED_STATUS_LABEL: Record<DischargeMedicationRow['status'], string> = {
  new: 'New', changed: 'Changed', continued: 'Continued', stopped: 'Stopped',
};

// The "▸ Structured fields" disclosure — every field the structured editor
// can set, read-only, collapsed by default now that the readable document
// (or, lacking one, this same content) is the prominent view. Nothing here
// is lost, just no longer demanding attention before the doctor asks for it.
function StructuredFieldsSummary({ data, diagnoses }: { data: DischargeSummary; diagnoses: AdmissionDiagnosis[] }) {
  const linkedDiagnoses = (data.final_diagnosis_ids ?? [])
    .map((id) => diagnoses.find((d) => d.id === id))
    .filter((d): d is AdmissionDiagnosis => !!d);
  const row = (label: string, value: ReactNode) =>
    value ? <div key={label}><b>{label}:</b> {value}</div> : null;

  return (
    <div className="space-y-1.5 text-[12.5px]">
      {row('Final diagnosis', linkedDiagnoses.length
        ? linkedDiagnoses.map((d) => `${d.text}${d.role === 'principal' ? ' (principal)' : ''}`).join(', ')
        : null)}
      {row('Reason for admission', data.reason_for_admission)}
      {row('Significant findings', data.significant_findings)}
      {row('Investigations summary', data.investigations_summary)}
      {row('Procedures summary', data.procedures_summary)}
      {row('Hospital course', data.hospital_course)}
      {data.condition_at_discharge && row('Condition at discharge', [
        data.condition_at_discharge.vitals, data.condition_at_discharge.ambulation, data.condition_at_discharge.diet,
      ].filter(Boolean).join(' · ') || null)}
      {!!data.discharge_medications?.length && (
        <div>
          <b>Discharge medications:</b>
          <ul className="ml-4 list-disc">
            {data.discharge_medications.map((m, i) => (
              <li key={i}>{m.drug_name} {[m.dose, m.route, m.freq].filter(Boolean).join(' ')} — {MED_STATUS_LABEL[m.status]}</li>
            ))}
          </ul>
        </div>
      )}
      {!!data.follow_up?.length && (
        <div>
          <b>Follow-up:</b>
          <ul className="ml-4 list-disc">
            {data.follow_up.map((f, i) => <li key={i}>{[f.when, f.with, f.where, f.purpose].filter(Boolean).join(' · ')}</li>)}
          </ul>
        </div>
      )}
      {!!data.pending_results?.length && (
        <div>
          <b>Pending results:</b>
          <ul className="ml-4 list-disc">
            {data.pending_results.map((p, i) => <li key={i}>{[p.test, p.followed_by].filter(Boolean).join(' — ')}</li>)}
          </ul>
        </div>
      )}
      {data.advice && row('Advice', [data.advice.diet, data.advice.activity, data.advice.wound_care, data.advice.other].filter(Boolean).join(' · ') || null)}
      {row('Warning signs', data.warning_signs)}
      {row('Emergency contact', data.emergency_contact_text)}
      {row('Copy given to', data.copy_given_to)}
      {data.teach_back_done && row('Teach-back', 'Confirmed')}
      {data.discharge_type === 'death' && data.death && (
        <div className="rounded border border-danger/40 bg-danger-soft px-3 py-2">
          {row('Time of death', data.death.at)}
          {row('Immediate cause', data.death.immediate_cause)}
          {row('Antecedent cause', data.death.antecedent_cause)}
          {row('Underlying cause', data.death.underlying_cause)}
          {row('MCCD no.', data.death.mccd_no)}
        </div>
      )}
    </div>
  );
}

function useErr() {
  const [err, setErr] = useState<string | null>(null);
  // Unlike the other sections' copy of this helper, draftWithAI() below goes
  // through authFetch (a raw fetch, not lib/api/client.ts), so its failures
  // arrive as a plain Error, not an ApiError — surface that message too
  // rather than falling back to a generic one that hides, e.g., an AI
  // provider credential problem the doctor can't do anything about blind.
  const fail = (e: unknown) => setErr(e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.');
  const node = err ? <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div> : null;
  return { fail, node, clear: () => setErr(null) };
}

const DISCHARGE_TYPES: { value: DischargeType; label: string }[] = [
  { value: 'normal', label: 'Normal discharge' },
  { value: 'lama', label: 'LAMA' },
  { value: 'dama', label: 'DAMA' },
  { value: 'referred', label: 'Referred' },
  { value: 'death', label: 'Death' },
];
const MED_STATUSES: DischargeMedicationRow['status'][] = ['new', 'changed', 'continued', 'stopped'];

type Draft = DischargeSummaryInput;

// One dictation for the whole summary: a doctor at the discharge desk says
// the course, condition, advice and follow-up together rather than tapping
// through fifteen boxes. Discharge medications are deliberately NOT dictated
// — "Prefill from active orders" already carries the exact drug/dose/route
// from the chart, which is safer than a transcribed drug list. Time of death
// is not dictated either; a misheard timestamp on a death record is not a
// risk worth a saved tap.
const DS_TEXT_KEYS = ['reason_for_admission', 'significant_findings', 'investigations_summary', 'procedures_summary', 'hospital_course', 'warning_signs'] as const;
const DS_VOICE_FIELDS: VoiceField[] = [
  selectField('discharge_type', 'Type of discharge — normal, lama (left against medical advice), dama (discharged against medical advice), referred, death',
    DISCHARGE_TYPES.map(t => t.value)),
  textField('reason_for_admission', 'Reason for admission'),
  textField('significant_findings', 'Significant findings'),
  textField('investigations_summary', 'Investigations summary'),
  textField('procedures_summary', 'Procedures done'),
  textField('hospital_course', 'Course in hospital'),
  textField('cond_vitals', 'Condition at discharge — vitals'),
  textField('cond_ambulation', 'Condition at discharge — ambulation / mobility'),
  textField('cond_diet', 'Condition at discharge — diet tolerated'),
  textField('advice_diet', 'Advice — diet'),
  textField('advice_activity', 'Advice — activity'),
  textField('advice_wound_care', 'Advice — wound care'),
  textField('advice_other', 'Advice — anything else'),
  textField('warning_signs', 'Warning signs — return or call if'),
  textField('follow_up_when', 'Follow-up — when (e.g. after 1 week, on 25 Oct)'),
  textField('follow_up_with', 'Follow-up — with whom (doctor / department)'),
  textField('follow_up_purpose', 'Follow-up — purpose, e.g. review with reports, suture removal'),
  textField('pending_results', 'Results still pending, test names separated by semicolons'),
  textField('emergency_contact_text', 'Emergency contact'),
  textField('copy_given_to', 'Copy of the summary handed to (name / relation)'),
  yesNoField('teach_back_done', 'Patient / attender understanding confirmed (teach-back)'),
  textField('death_immediate_cause', 'Death — immediate cause'),
  textField('death_antecedent_cause', 'Death — antecedent cause'),
  textField('death_underlying_cause', 'Death — underlying cause'),
];

function applyDischargeVoice(p: Draft, x: Extracted): Draft {
  const next: Draft = { ...p, ...mergeTextFields(p, x, [...DS_TEXT_KEYS]) };
  const type = vOneOf(x, 'discharge_type', DISCHARGE_TYPES.map(t => t.value));
  if (type) next.discharge_type = type;

  const cond = { ...(p.condition_at_discharge ?? {}) };
  for (const k of ['vitals', 'ambulation', 'diet'] as const) {
    const t = vText(x, `cond_${k}`);
    if (t) cond[k] = appendText(cond[k], t, '; ');
  }
  next.condition_at_discharge = cond;

  const advice = { ...(p.advice ?? {}) };
  for (const k of ['diet', 'activity', 'wound_care', 'other'] as const) {
    const t = vText(x, `advice_${k}`);
    if (t) advice[k] = appendText(advice[k], t, '; ');
  }
  next.advice = advice;

  const fu: FollowUpRow = { when: vText(x, 'follow_up_when'), with: vText(x, 'follow_up_with'), purpose: vText(x, 'follow_up_purpose') };
  if (fu.when || fu.with || fu.purpose) next.follow_up = [...(p.follow_up ?? []), fu];

  const pending = vText(x, 'pending_results');
  if (pending) {
    const have = new Set((p.pending_results ?? []).map(r => (r.test ?? '').toLowerCase()));
    const add = pending.split(/[;\n]+/).map(t => t.trim()).filter(t => t && !have.has(t.toLowerCase())).map(test => ({ test }));
    next.pending_results = [...(p.pending_results ?? []), ...add];
  }

  for (const k of ['emergency_contact_text', 'copy_given_to'] as const) {
    const t = vText(x, k);
    if (t) next[k] = t;
  }
  const teach = vBool(x, 'teach_back_done');
  if (teach !== undefined) next.teach_back_done = teach;

  const death = { ...(p.death ?? {}) };
  let deathChanged = false;
  for (const k of ['immediate_cause', 'antecedent_cause', 'underlying_cause'] as const) {
    const t = vText(x, `death_${k}`);
    if (t) { death[k] = appendText(death[k], t, '; '); deathChanged = true; }
  }
  if (deathChanged) next.death = death;
  return next;
}

const EMPTY_DRAFT: Draft = {
  discharge_type: 'normal',
  final_diagnosis_ids: [],
  reason_for_admission: '', significant_findings: '', investigations_summary: '', procedures_summary: '', hospital_course: '',
  condition_at_discharge: {}, discharge_medications: [], follow_up: [], pending_results: [],
  advice: {}, warning_signs: '', emergency_contact_text: '', advice_language: '',
  teach_back_done: false, copy_given_to: '', death: {},
};


export function DischargeSummarySection({ admissionId, readOnly, patientUhid, chatOpen, onChatOpenChange }: {
  admissionId: number; readOnly: boolean;
  /** For the "👁 View / ⬇ Download" link on the last approved .docx. */
  patientUhid: string;
  // Chat-open is owned by CaseSheetWorkspace, not this component — opening
  // it collapses the workspace's own header/alerts/snapshot/tab-strip down
  // to a minimal back-bar, exactly like IPModule.tsx's dedicated discharge-
  // summary step does, so the chat gets the full screen rather than fighting
  // this section's structured form for space. See CaseSheetWorkspace.tsx.
  chatOpen: boolean; onChatOpenChange: (open: boolean) => void;
}) {
  const can = useCan();
  const mayWrite = can('ip:discharge-summary') && !readOnly;
  const { data, isLoading, refetch } = useDischargeSummary(admissionId);
  // Always enabled, not gated on mayWrite — needed to resolve
  // final_diagnosis_ids for display in the read view too, which anyone who
  // can open this tab should be able to see regardless of write access.
  const { data: diagnoses } = useDiagnoses(admissionId);
  const { data: activeOrders } = useMedicationOrders(admissionId, 'active', mayWrite);
  const put = usePutDischargeSummary(admissionId);
  const sign = useSignDischargeSummary(admissionId);
  const amend = useAmendDischargeSummary(admissionId);
  const { fail, node, clear } = useErr();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  // Saving a signed summary asks for the amendment reason in a dialog —
  // window.prompt() is refused by embedded browsers. Holds the edits to save.
  const [amendPayload, setAmendPayload] = useState<Parameters<typeof amend.mutate>[0]['changes'] | null>(null);
  const [deceasedWarning, setDeceasedWarning] = useState<string | null>(null);
  // Local, not native <details> — a native <summary> can't contain a second
  // interactive element (the Edit button below), and union with the
  // top-level Continue/Amend action in the header isn't enough: that button
  // scrolled out of view behind the readable document, so there was no
  // visible way to edit from where the structured fields actually are.
  const [showStructured, setShowStructured] = useState(false);

  // The full AI chat (streaming, Doctor AI Memory, suggestion chips,
  // proposal cards) is the SAME DischargeSummaryChat.tsx embedded in
  // IPModule.tsx's Charges & discharge screen — reused here, not
  // reimplemented, so a doctor drafting from the Case Sheet gets the exact
  // same assistant. It needs the richer `Admission` shape (charges,
  // origin_visit) that useCaseSheet's CaseSheet doesn't carry, fetched
  // separately only once the panel is actually opened.
  const [chatAdmission, setChatAdmission] = useState<ChatAdmission | null>(null);
  useEffect(() => {
    if (!chatOpen || chatAdmission) return;
    getAdmission(admissionId).then(setChatAdmission).catch((e) => fail(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen, admissionId]);

  const currentDiagnoses = useMemo(() => (diagnoses ?? []).filter((d) => !d.superseded), [diagnoses]);
  const isSigned = data?.status === 'signed';
  const isDeath = draft.discharge_type === 'death';

  // Takes an explicit row rather than always reading the `data` closure, so
  // onChatApproved below can open the editor on the result refetch() just
  // resolved with — the query's own `data` wouldn't reflect that until the
  // next render, one tick too late for the chain "refetch, then edit".
  const startEdit = (row = data) => {
    setDraft(row ? {
      discharge_type: row.discharge_type,
      final_diagnosis_ids: row.final_diagnosis_ids ?? [],
      reason_for_admission: row.reason_for_admission ?? '',
      significant_findings: row.significant_findings ?? '',
      investigations_summary: row.investigations_summary ?? '',
      procedures_summary: row.procedures_summary ?? '',
      hospital_course: row.hospital_course ?? '',
      condition_at_discharge: row.condition_at_discharge ?? {},
      discharge_medications: row.discharge_medications ?? [],
      follow_up: row.follow_up ?? [],
      pending_results: row.pending_results ?? [],
      advice: row.advice ?? {},
      warning_signs: row.warning_signs ?? '',
      emergency_contact_text: row.emergency_contact_text ?? '',
      advice_language: row.advice_language ?? '',
      teach_back_done: row.teach_back_done ?? false,
      copy_given_to: row.copy_given_to ?? '',
      death: row.death ?? {},
    } : EMPTY_DRAFT);
    setEditing(true);
    clear();
  };

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }));
  const setCondition = (k: 'vitals' | 'ambulation' | 'diet' | 'mental_status', v: string) =>
    setDraft((p) => ({ ...p, condition_at_discharge: { ...p.condition_at_discharge, [k]: v } }));
  const setAdvice = (k: 'diet' | 'activity' | 'wound_care' | 'other', v: string) =>
    setDraft((p) => ({ ...p, advice: { ...p.advice, [k]: v } }));
  const setDeath = (k: keyof NonNullable<Draft['death']>, v: string) =>
    setDraft((p) => ({ ...p, death: { ...p.death, [k]: v } }));

  const toggleDiagnosis = (id: number) => setDraft((p) => {
    const ids = p.final_diagnosis_ids ?? [];
    return { ...p, final_diagnosis_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
  });

  const prefillMedications = () => {
    const rows: DischargeMedicationRow[] = (activeOrders ?? []).map((o) => ({
      drug_name: o.drug_name,
      dose: [o.dose_quantity, o.dose_unit].filter(Boolean).join(' '),
      route: o.route ?? '',
      freq: o.frequency_text ?? o.frequency_code ?? '',
      duration: '',
      status: 'continued',
      instruction: '',
    }));
    set('discharge_medications', rows);
  };
  const updateMedRow = (i: number, patch: Partial<DischargeMedicationRow>) =>
    setDraft((p) => ({ ...p, discharge_medications: (p.discharge_medications ?? []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const removeMedRow = (i: number) =>
    setDraft((p) => ({ ...p, discharge_medications: (p.discharge_medications ?? []).filter((_, idx) => idx !== i) }));
  const addMedRow = () =>
    setDraft((p) => ({ ...p, discharge_medications: [...(p.discharge_medications ?? []), { drug_name: '', status: 'new' as const }] }));

  const updateFollowUp = (i: number, patch: Partial<FollowUpRow>) =>
    setDraft((p) => ({ ...p, follow_up: (p.follow_up ?? []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const removeFollowUp = (i: number) => setDraft((p) => ({ ...p, follow_up: (p.follow_up ?? []).filter((_, idx) => idx !== i) }));
  const addFollowUp = () => setDraft((p) => ({ ...p, follow_up: [...(p.follow_up ?? []), {}] }));

  const updatePending = (i: number, patch: Partial<PendingResultRow>) =>
    setDraft((p) => ({ ...p, pending_results: (p.pending_results ?? []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const removePending = (i: number) => setDraft((p) => ({ ...p, pending_results: (p.pending_results ?? []).filter((_, idx) => idx !== i) }));
  const addPending = () => setDraft((p) => ({ ...p, pending_results: [...(p.pending_results ?? []), {}] }));

  // The chat writes straight to the server (consultations + the structured
  // draft sync, see DischargeSummaryChat.tsx's approve()) — it never goes
  // through local `draft` state. On approve, pull the freshly-synced fields
  // back in: refetch, then (re)open the structured editor on whatever just
  // landed so the doctor can add medications/follow-up/diagnoses and sign.
  // After approving, drop back out of focus mode into the normal tab —
  // straight onto the structured editor, pre-filled with whatever the chat
  // just synced, so the doctor lands somewhere that shows the result rather
  // than the now-empty chat screen.
  const onChatApproved = () => {
    refetch().then((result) => { if (result.data) startEdit(result.data); });
    onChatOpenChange(false);
  };

  // Focus mode: CaseSheetWorkspace has already collapsed its own header/
  // alerts/snapshot/tab-strip down to a minimal back-bar for this (see
  // there) — this section itself renders nothing but the chat, full height,
  // same as IPModule.tsx's dedicated discharge-summary step.
  if (chatOpen) {
    return (
      <div className="flex min-h-[520px] flex-col gap-3">
        {chatAdmission ? (
          <DischargeSummaryChat admission={chatAdmission} onApproved={onChatApproved} />
        ) : <LoadingState label="Loading admission…" />}
      </div>
    );
  }

  if (isLoading) return <Section title="Discharge summary"><LoadingState label="Loading discharge summary…" /></Section>;

  return (
    <Section
      focusId="discharge_summary"
      title="Discharge summary"
      subtitle={data
        ? isSigned
          ? `Signed by ${data.signed_by} · ${fmtDateTime(data.signed_at)}${data.version > 1 ? ` · v${data.version}` : ''}`
          : 'Draft — not yet signed'
        : 'Not started'}
      action={
        <>
          {mayWrite && <Button variant="ghost" size="sm" onClick={() => onChatOpenChange(true)}>🤖 AI Assistant</Button>}
          {mayWrite && !editing && !isSigned && <Button size="sm" data-focus="discharge_summary:edit" onClick={() => startEdit()}>{data ? 'Continue' : 'Start discharge summary'}</Button>}
          {mayWrite && isSigned && !editing && <Button size="sm" onClick={() => startEdit()}>Amend</Button>}
        </>
      }
    >
      {isSigned && !editing && (
        <div className="mb-3 rounded border border-success/40 bg-success-soft px-3 py-1.5 text-[11.5px] text-success-ink">
          Signed — this discharge summary can no longer be edited. Use Amend to correct it (creates a new version).
        </div>
      )}
      {deceasedWarning && !editing && (
        <div className="mb-3 rounded border border-warn/40 bg-warn-soft px-3 py-1.5 text-[11.5px] text-warn-ink">
          ⚠ {deceasedWarning}
        </div>
      )}
      {node}

      {!editing ? (
        data ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={data.discharge_type === 'death' ? 'danger' : 'neutral'}>
                {DISCHARGE_TYPES.find((t) => t.value === data.discharge_type)?.label ?? data.discharge_type}
              </Badge>
              {data.source === 'ai_assisted' && <Badge variant="neutral">AI-assisted draft</Badge>}
              {data.document_id != null && (
                <DocumentPreviewButtons uhid={patientUhid} docId={data.document_id} label="discharge summary" />
              )}
            </div>

            {/* The readable document is the prominent view — same content a
                doctor approved in the chat, neatly formatted, open by
                default. Every structured field is still here, just
                collapsed below rather than competing for attention. Falls
                back to the old inline summary when there's no generated
                text to show (a summary authored structured-only, never
                through the chat). */}
            {data.ai_generated_text ? (
              <ReadableDocument text={data.ai_generated_text} />
            ) : (
              <div className="space-y-1.5 text-[12.5px]">
                {data.reason_for_admission && <div><b>Reason:</b> {data.reason_for_admission}</div>}
                {data.hospital_course && <div><b>Course:</b> {data.hospital_course}</div>}
              </div>
            )}
            {data.discharge_type === 'death' && data.death?.at && (
              <div className="rounded border border-danger/40 bg-danger-soft px-3 py-2 text-[12.5px]">
                <b>Death:</b> {data.death.at} · {data.death.immediate_cause || '—'}
              </div>
            )}

            <div className="rounded-lg border border-border bg-muted/30 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <button type="button" className="cursor-pointer text-[12px] font-semibold text-ink-muted"
                  onClick={() => setShowStructured((v) => !v)}>
                  {showStructured ? '▾' : '▸'} Structured fields
                </button>
                {mayWrite && !isSigned && (
                  <Button variant="ghost" size="sm" onClick={() => { setShowStructured(true); startEdit(); }}>✎ Edit</Button>
                )}
              </div>
              {showStructured && (
                <div className="mt-2">
                  <StructuredFieldsSummary data={data} diagnoses={diagnoses ?? []} />
                </div>
              )}
            </div>
          </div>
        ) : <Empty label="No discharge summary started" />
      ) : (
        <div className="space-y-4">
          {data?.ai_generated_text && (
            <GeneratedTextDetails text={data.ai_generated_text}
              hint="The AI assistant only recognizes certain section headings — anything it drafted that isn't reflected in the fields below is still here, verbatim, to copy across or cross-check against." />
          )}
          <VoiceFormFiller fields={DS_VOICE_FIELDS} label="🎙 Dictate discharge summary"
            hint="Course uneventful, fever settled on day 3; ambulant, taking normal diet; advise soft diet, avoid heavy lifting; review after 1 week with Dr. Menon; return if fever or breathlessness"
            onFields={(x) => setDraft((p) => applyDischargeVoice(p, x))} />
          <div className="grid gap-2 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold">Discharge type</label>
              <NativeSelect value={draft.discharge_type} onChange={(e) => set('discharge_type', e.target.value as DischargeType)}>
                {DISCHARGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </NativeSelect>
            </div>
            <div className="sm:col-span-2 self-end">
              <Button variant="ghost" size="sm" onClick={() => onChatOpenChange(true)}>🤖 Open AI Assistant to draft the narrative</Button>
            </div>
          </div>

          <div data-focus="final_diagnosis">
            <label className="mb-1 block text-xs font-semibold">Final diagnosis</label>
            <div className="flex flex-wrap gap-2">
              {currentDiagnoses.length === 0 && <span className="text-[12px] text-ink-muted">No diagnoses recorded yet (Assessment tab).</span>}
              {currentDiagnoses.map((d) => (
                <label key={d.id} className="flex items-center gap-1.5 rounded border border-border px-2 py-1 text-[12px]">
                  <input type="checkbox" checked={(draft.final_diagnosis_ids ?? []).includes(d.id)} onChange={() => toggleDiagnosis(d.id)} />
                  {d.text}{d.role === 'principal' ? ' (principal)' : ''}
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs font-semibold">Reason for admission</label>
              <Textarea rows={2} value={draft.reason_for_admission ?? ''} onChange={(e) => set('reason_for_admission', e.target.value)} /></div>
            <div><label className="mb-1 block text-xs font-semibold">Significant findings</label>
              <Textarea rows={2} value={draft.significant_findings ?? ''} onChange={(e) => set('significant_findings', e.target.value)} /></div>
          </div>

          {!isDeath && (
            <div className="grid gap-2 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-semibold">Investigations summary</label>
                <Textarea rows={2} value={draft.investigations_summary ?? ''} onChange={(e) => set('investigations_summary', e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-semibold">Procedures summary</label>
                <Textarea rows={2} value={draft.procedures_summary ?? ''} onChange={(e) => set('procedures_summary', e.target.value)} /></div>
            </div>
          )}

          <div><label className="mb-1 block text-xs font-semibold">Hospital course</label>
            <Textarea rows={3} value={draft.hospital_course ?? ''} onChange={(e) => set('hospital_course', e.target.value)} /></div>

          {isDeath ? (
            <div className="rounded-lg border border-danger/40 bg-danger-soft p-3">
              <div className="mb-2 text-[12.5px] font-semibold text-danger-ink">Death summary</div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div><label className="mb-1 block text-xs font-semibold">Time of death</label>
                  <Input type="datetime-local" value={draft.death?.at ?? ''} onChange={(e) => setDeath('at', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">MCCD no.</label>
                  <Input value={draft.death?.mccd_no ?? ''} onChange={(e) => setDeath('mccd_no', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Immediate cause</label>
                  <Input value={draft.death?.immediate_cause ?? ''} onChange={(e) => setDeath('immediate_cause', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Antecedent cause</label>
                  <Input value={draft.death?.antecedent_cause ?? ''} onChange={(e) => setDeath('antecedent_cause', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Underlying cause</label>
                  <Input value={draft.death?.underlying_cause ?? ''} onChange={(e) => setDeath('underlying_cause', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Resuscitation record ref.</label>
                  <Input value={draft.death?.resus_ref ?? ''} onChange={(e) => setDeath('resus_ref', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Body handed over to</label>
                  <Input value={draft.death?.handed_over_to ?? ''} onChange={(e) => setDeath('handed_over_to', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Handed over at</label>
                  <Input type="datetime-local" value={draft.death?.handed_over_at ?? ''} onChange={(e) => setDeath('handed_over_at', e.target.value)} /></div>
              </div>
              <div className="mt-2 text-[11px] text-ink-muted">Signing a death summary records the patient as deceased.</div>
            </div>
          ) : (
            <>
              <div data-focus="discharge_medications">
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-semibold">Discharge medications</label>
                  <Button variant="ghost" size="sm" onClick={prefillMedications} disabled={!activeOrders?.length}>
                    Prefill from active orders ({activeOrders?.length ?? 0})
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {(draft.discharge_medications ?? []).map((m, i) => (
                    <div key={i} className="grid grid-cols-2 items-center gap-1.5 rounded border border-border p-1.5 sm:grid-cols-[1.4fr_0.8fr_0.6fr_0.6fr_0.8fr_1fr_auto] sm:rounded-none sm:border-0 sm:p-0">
                      <Input className="col-span-2 sm:col-span-1" placeholder="Drug" value={m.drug_name} onChange={(e) => updateMedRow(i, { drug_name: e.target.value })} />
                      <Input placeholder="Dose" value={m.dose ?? ''} onChange={(e) => updateMedRow(i, { dose: e.target.value })} />
                      <Input placeholder="Route" value={m.route ?? ''} onChange={(e) => updateMedRow(i, { route: e.target.value })} />
                      <Input placeholder="Freq" value={m.freq ?? ''} onChange={(e) => updateMedRow(i, { freq: e.target.value })} />
                      <Input placeholder="Duration" value={m.duration ?? ''} onChange={(e) => updateMedRow(i, { duration: e.target.value })} />
                      <NativeSelect value={m.status} onChange={(e) => updateMedRow(i, { status: e.target.value as DischargeMedicationRow['status'] })}>
                        {MED_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </NativeSelect>
                      <Button variant="ghost" size="sm" onClick={() => removeMedRow(i)}>✕</Button>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="mt-1.5" onClick={addMedRow}>+ Medicine row</Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div data-focus="follow_up">
                  <label className="mb-1 block text-xs font-semibold">Follow-up</label>
                  <div className="space-y-1.5">
                    {(draft.follow_up ?? []).map((f, i) => (
                      <div key={i} className="grid grid-cols-2 gap-1.5 rounded border border-border p-1.5 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] sm:rounded-none sm:border-0 sm:p-0">
                        <Input placeholder="When" value={f.when ?? ''} onChange={(e) => updateFollowUp(i, { when: e.target.value })} />
                        <Input placeholder="With" value={f.with ?? ''} onChange={(e) => updateFollowUp(i, { with: e.target.value })} />
                        <Input placeholder="Where" value={f.where ?? ''} onChange={(e) => updateFollowUp(i, { where: e.target.value })} />
                        <Input placeholder="Purpose" value={f.purpose ?? ''} onChange={(e) => updateFollowUp(i, { purpose: e.target.value })} />
                        <Button variant="ghost" size="sm" onClick={() => removeFollowUp(i)}>✕</Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-1.5" onClick={addFollowUp}>+ Follow-up</Button>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold">Pending results</label>
                  <div className="space-y-1.5">
                    {(draft.pending_results ?? []).map((p, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1.5">
                        <Input placeholder="Test" value={p.test ?? ''} onChange={(e) => updatePending(i, { test: e.target.value })} />
                        <Input placeholder="Followed by" value={p.followed_by ?? ''} onChange={(e) => updatePending(i, { followed_by: e.target.value })} />
                        <Button variant="ghost" size="sm" onClick={() => removePending(i)}>✕</Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-1.5" onClick={addPending}>+ Pending result</Button>
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div><label className="mb-1 block text-xs font-semibold">Advice — diet</label>
                  <Textarea rows={2} value={draft.advice?.diet ?? ''} onChange={(e) => setAdvice('diet', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Advice — activity</label>
                  <Textarea rows={2} value={draft.advice?.activity ?? ''} onChange={(e) => setAdvice('activity', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Advice — wound care</label>
                  <Textarea rows={2} value={draft.advice?.wound_care ?? ''} onChange={(e) => setAdvice('wound_care', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Advice — other</label>
                  <Textarea rows={2} value={draft.advice?.other ?? ''} onChange={(e) => setAdvice('other', e.target.value)} /></div>
              </div>

              <div><label className="mb-1 block text-xs font-semibold">Warning signs — return/call if</label>
                <Textarea rows={2} value={draft.warning_signs ?? ''} onChange={(e) => set('warning_signs', e.target.value)} /></div>

              <div className="grid gap-2 sm:grid-cols-3">
                <div><label className="mb-1 block text-xs font-semibold">Condition — vitals</label>
                  <Input value={draft.condition_at_discharge?.vitals ?? ''} onChange={(e) => setCondition('vitals', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Condition — ambulation</label>
                  <Input value={draft.condition_at_discharge?.ambulation ?? ''} onChange={(e) => setCondition('ambulation', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Condition — diet</label>
                  <Input value={draft.condition_at_discharge?.diet ?? ''} onChange={(e) => setCondition('diet', e.target.value)} /></div>
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <div><label className="mb-1 block text-xs font-semibold">Emergency contact</label>
                  <Input value={draft.emergency_contact_text ?? ''} onChange={(e) => set('emergency_contact_text', e.target.value)} /></div>
                <div><label className="mb-1 block text-xs font-semibold">Copy given to</label>
                  <Input value={draft.copy_given_to ?? ''} onChange={(e) => set('copy_given_to', e.target.value)} /></div>
                <label className="flex items-center gap-1.5 self-end pb-2 text-[12.5px]">
                  <input type="checkbox" checked={!!draft.teach_back_done} onChange={(e) => set('teach_back_done', e.target.checked)} />
                  Understanding confirmed (teach-back)
                </label>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); clear(); }}>Cancel</Button>
            <Button size="sm" loading={put.isPending}
              onClick={() => {
                clear();
                const payload = { ...draft, copy_given_at: draft.copy_given_to ? new Date().toISOString() : undefined };
                if (isSigned && data) {
                  amend.reset();
                  setAmendPayload(payload);
                } else {
                  put.mutate(payload, { onSuccess: () => setEditing(false), onError: fail });
                }
              }}>Save</Button>
            {mayWrite && !isSigned && data && (
              <Button variant="success" size="sm" loading={sign.isPending}
                onClick={() => {
                  clear();
                  // Save first so Sign always acts on the latest edits, not a
                  // possibly-stale server copy from before this editing session.
                  put.mutate(draft, {
                    onSuccess: (saved) => sign.mutate(saved.id, {
                      onSuccess: (signed) => { setDeceasedWarning(signed.deceased_flag_warning ?? null); setEditing(false); },
                      onError: fail,
                    }),
                    onError: fail,
                  });
                }}>Save &amp; sign</Button>
            )}
          </div>
        </div>
      )}
      <TextPromptDialog
        open={!!amendPayload}
        title="Amend the signed discharge summary?"
        description="The signed version is kept unchanged; your edits are saved as a new, amended version."
        label="Why is this discharge summary being amended?"
        multiline
        confirmLabel="Save amendment"
        pending={amend.isPending}
        error={amend.error instanceof Error ? amend.error.message : null}
        onConfirm={(reason) => {
          if (!amendPayload || !data) return;
          amend.mutate({ summaryId: data.id, amendment_reason: reason, changes: amendPayload }, {
            onSuccess: () => { setAmendPayload(null); setEditing(false); },
          });
        }}
        onClose={() => setAmendPayload(null)}
      />
    </Section>
  );
}
